import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router, RoutesRecognized } from '@angular/router';
import { faGenderless } from '@fortawesome/free-solid-svg-icons/faGenderless';
import { SBRouteData, SideNavItem } from '@modules/navigation/models';
import { NavigationService, SideNavService } from '@modules/navigation/services';
import moment from 'moment/moment';
import objectHash from 'object-hash';
import { Subscription } from 'rxjs';
import { filter, pairwise } from 'rxjs/operators';

@Component({
    selector: 'sbpro-side-nav-item',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './side-nav-item-pro.component.html',
    styleUrls: ['side-nav-item-pro.component.scss'],
    standalone: false
})
export class SideNavItemProComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('collapsibleSection') collapsibleSection!: ElementRef<HTMLDivElement>;

  @Input() sideNavItem!: SideNavItem;
  @Input() hierarchy: string[] = [];
  @Input() isFeatherIcon = true;

  isActive = false;
  collapsed!: boolean;
  routeData!: SBRouteData;
  navElement!: HTMLDivElement;
  id!: string;
  hierarchyExtension!: string[];
  subscription: Subscription = new Subscription();
  afterViewInit = false;

  genderless = faGenderless;

  constructor(private router: Router,
              private ar: ActivatedRoute,
              private sideNavService: SideNavService,
              public navigationService: NavigationService,
              private changeDetectorRef: ChangeDetectorRef) {
  }

  ngOnInit() {
    this.id = objectHash(this.sideNavItem);
    this.collapsed = !this.sideNavService.isExpanded(this.id);
    this.hierarchyExtension = [...this.hierarchy, this.id];
    this.subscription.add(
      this.sideNavService.expand$.subscribe(ids => {
        const thisIDisInExpandedHierarchy = !!ids.find(id => id === this.id);
        const sameLevel = ids.length === this.hierarchyExtension.length;

        if (!thisIDisInExpandedHierarchy && sameLevel) {
          this.collapse();
        }
      })
    );
    this.subscription.add(
      this.navigationService
        .currentURL$()
        .subscribe({
            next: (currentURL: string) =>
              this.determineIfActive(currentURL.replace(/#.*$/, ''), this.sideNavItem)
          }
        )
    );
  }

  ngAfterViewInit() {
    this.afterViewInit = true;
    this.navElement = this.collapsibleSection.nativeElement;
    this.navElement.addEventListener('transitionend', () => {
      if (!this.collapsed) {
        this.navElement.style.height = '';
      }
    });
    if (this.isActive) {
      this.expand();
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  determineIfActive(url: string, sideNavItem: SideNavItem) {
    this.isActive = false;
    this.changeDetectorRef.markForCheck();
    if (sideNavItem.link) {
      this.isActive = url.includes(sideNavItem.link);
      this.changeDetectorRef.markForCheck();
    }
    if (sideNavItem.submenu) {
      sideNavItem.submenu.forEach(submenu => this.determineIfActiveParent(url, submenu));

      if (!this.isActive) {
        this.router.events
          .pipe(filter(event => event instanceof RoutesRecognized), pairwise())
          .subscribe((event: any[]) => {
            const previousUrl: string = event[0].urlAfterRedirects || '';
            this.isActive = url.substring(0, previousUrl.length).includes(previousUrl);

            // TODO: refactor set 'active' em item menu, submodule
            sideNavItem.submenu.filter(submenu => {
              return submenu.link === previousUrl;
            });
          });
      }
    }
  }

  determineIfActiveParent(url: string, sideNavItem: SideNavItem) {
    if (sideNavItem.link && url === sideNavItem.link) {
      this.isActive = true;
      if (this.afterViewInit) {
        this.expand();
      }
      this.changeDetectorRef.markForCheck();
    }
    if (sideNavItem.submenu) {
      sideNavItem.submenu.forEach(submenu => this.determineIfActiveParent(url, submenu));
    }
  }

  toggle() {
    if (this.sideNavItem.link) {
      return this.router.navigate([this.sideNavItem.link]);
    }

    if (this.collapsed) {
      return this.expand();
    }
    return this.collapse();
  }

  collapse() {
    this.sideNavService.setExpanded(this.id, false);
    if (this.navElement) {
      const navHeight = this.navElement.scrollHeight;
      const elementTransition = this.navElement.style.transition;
      this.navElement.style.transition = '';
      requestAnimationFrame(() => {
        this.navElement.style.height = navHeight + 'px';
        this.navElement.style.transition = elementTransition;
        requestAnimationFrame(() => {
          this.navElement.style.height = 0 + 'px';
          this.collapsed = true;
          this.changeDetectorRef.markForCheck();
        });
      });
    }
  }

  expand() {
    this.sideNavService.setExpanded(this.id, true);
    this.navElement.style.height = this.navElement.scrollHeight + 'px';
    this.collapsed = false;
    this.sideNavService.expand$.next(this.hierarchyExtension);
  }

  validateNew(item: SideNavItem): boolean {
    if (item.new && typeof item.new !== 'boolean') {
      return item.new.isAfter(moment());
    }
    return !!item.new;
  }
}
